import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Badge, 
  Button, 
  Spinner, 
  Form,
  ProgressBar,
  Modal,
  Alert
} from 'react-bootstrap';
import { 
  FiCheck, 
  FiX, 
  FiArrowLeft, 
  FiCalendar,
  FiUser,
  FiClock,
  FiMessageSquare
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import hearingChecklistService, { 
  HearingChecklistItem, 
  HearingChecklistSummary 
} from '../../services/hearing-checklist.service';

const HearingChecklist: React.FC = () => {
  const { hearingId } = useParams<{ hearingId: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<HearingChecklistItem[]>([]);
  const [summary, setSummary] = useState<HearingChecklistSummary | null>(null);
  
  // Modal para notas
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<HearingChecklistItem | null>(null);
  const [itemNotes, setItemNotes] = useState('');

  useEffect(() => {
    if (hearingId) {
      loadChecklist();
    }
  }, [hearingId]);

  const loadChecklist = async () => {
    try {
      setLoading(true);
      const response = await hearingChecklistService.getChecklistByHearing(Number(hearingId));
      setItems(response.items);
      setSummary(response.summary);
    } catch (error) {
      console.error('Error loading checklist:', error);
      toast.error('Error al cargar el checklist');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (item: HearingChecklistItem) => {
    try {
      setSaving(true);
      const response = await hearingChecklistService.toggleChecklistItem(
        Number(hearingId),
        item.template_id,
        !item.is_completed
      );
      
      // Update local state
      setItems(prev => prev.map(i => 
        i.id === item.id 
          ? { ...i, is_completed: !item.is_completed, completed_at: !item.is_completed ? new Date().toISOString() : undefined }
          : i
      ));
      setSummary(response.summary);
      
      toast.success(item.is_completed ? 'Item desmarcado' : 'Item completado');
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error('Error al actualizar el checklist');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenNotes = (item: HearingChecklistItem) => {
    setSelectedItem(item);
    setItemNotes(item.notes || '');
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedItem) return;
    
    try {
      setSaving(true);
      await hearingChecklistService.updateChecklistItem(selectedItem.id, { notes: itemNotes });
      
      // Update local state
      setItems(prev => prev.map(i => 
        i.id === selectedItem.id ? { ...i, notes: itemNotes } : i
      ));
      
      setShowNotesModal(false);
      toast.success('Notas guardadas');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Error al guardar notas');
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const roleMap: Record<string, { bg: string; label: string }> = {
      abogado: { bg: 'primary', label: 'Abogado' },
      auxiliar: { bg: 'info', label: 'Auxiliar' },
      coordinador: { bg: 'warning', label: 'Coordinador' }
    };
    const config = roleMap[role] || { bg: 'secondary', label: role };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  const completionPct = summary?.completion_pct || 0;
  const progressVariant = completionPct === 100 ? 'success' : completionPct >= 50 ? 'warning' : 'danger';

  return (
    <div className="hearing-checklist">
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <Button 
              variant="link" 
              className="p-0 mb-2 text-muted"
              onClick={() => navigate(-1)}
            >
              <FiArrowLeft className="me-1" /> Volver
            </Button>
            <h4 className="page-title mb-1">Checklist de Audiencia</h4>
            {summary && (
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
                  <li className="breadcrumb-item"><Link to="/hearings">Audiencias</Link></li>
                  <li className="breadcrumb-item active">{summary.hearing_type}</li>
                </ol>
              </nav>
            )}
          </div>
        </div>
      </div>

      {/* Summary Card */}
      {summary && (
        <Card className="mb-4">
          <Card.Body>
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">{summary.hearing_type}</h5>
                <div className="mb-2">
                  <FiCalendar className="me-2 text-primary" />
                  <strong>{formatDate(summary.scheduled_date)}</strong>
                  {summary.scheduled_time && (
                    <span className="ms-2 text-muted">
                      <FiClock className="me-1" />
                      {formatTime(summary.scheduled_time)}
                    </span>
                  )}
                </div>
                <div className="mb-2">
                  <FiUser className="me-2 text-primary" />
                  Cliente: <strong>{summary.client_name || 'N/A'}</strong>
                </div>
                <div>
                  Caso: <Link to={`/cases/${summary.case_number}`}>{summary.case_number}</Link>
                </div>
              </div>
              <div className="col-md-6">
                <div className="mb-2">
                  <strong>Progreso del Checklist</strong>
                </div>
                <ProgressBar 
                  now={completionPct} 
                  variant={progressVariant}
                  label={`${Math.round(completionPct)}%`}
                  className="mb-2"
                  style={{ height: '25px' }}
                />
                <div className="text-muted">
                  {summary.completed_items} de {summary.total_items} items completados
                </div>
                {completionPct === 100 && (
                  <Alert variant="success" className="mt-2 mb-0 py-2">
                    <FiCheck className="me-2" />
                    ¡Checklist completado!
                  </Alert>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Checklist Items */}
      <Card>
        <Card.Header className="bg-white">
          <h5 className="mb-0">Items del Checklist</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '50px' }}>#</th>
                <th style={{ width: '60px' }}>Estado</th>
                <th>Actividad</th>
                <th style={{ width: '120px' }}>Responsable</th>
                <th style={{ width: '150px' }}>Completado</th>
                <th style={{ width: '100px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr 
                  key={item.id} 
                  className={item.is_completed ? 'table-success' : ''}
                >
                  <td className="text-center text-muted">{index + 1}</td>
                  <td className="text-center">
                    <Form.Check
                      type="checkbox"
                      checked={item.is_completed}
                      onChange={() => handleToggleItem(item)}
                      disabled={saving}
                      className="d-inline-block"
                      style={{ transform: 'scale(1.3)' }}
                    />
                  </td>
                  <td>
                    <div className={item.is_completed ? 'text-decoration-line-through text-muted' : ''}>
                      <strong>{item.title}</strong>
                    </div>
                    {item.description && (
                      <small className="text-muted d-block">{item.description}</small>
                    )}
                    {item.notes && (
                      <small className="text-info d-block mt-1">
                        <FiMessageSquare className="me-1" />
                        {item.notes}
                      </small>
                    )}
                  </td>
                  <td>{getRoleBadge(item.responsible_role)}</td>
                  <td>
                    {item.is_completed ? (
                      <div>
                        <small className="text-success">
                          <FiCheck className="me-1" />
                          {formatDate(item.completed_at)}
                        </small>
                        {item.completed_by_name && (
                          <small className="d-block text-muted">{item.completed_by_name}</small>
                        )}
                      </div>
                    ) : (
                      <Badge bg="secondary">Pendiente</Badge>
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handleOpenNotes(item)}
                      title="Agregar notas"
                    >
                      <FiMessageSquare />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Notes Modal */}
      <Modal show={showNotesModal} onHide={() => setShowNotesModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Notas del Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <>
              <div className="mb-3">
                <strong>{selectedItem.title}</strong>
              </div>
              <Form.Group>
                <Form.Label>Observaciones</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={itemNotes}
                  onChange={(e) => setItemNotes(e.target.value)}
                  placeholder="Ingrese observaciones o notas sobre este item..."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNotesModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveNotes} disabled={saving}>
            {saving ? <Spinner size="sm" /> : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HearingChecklist;
